import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, TrendingUp, Users, Award, Shield, Scale, Star, CheckCircle } from 'lucide-react';
import ConsultationPopup from './ConsultationPopup';

// Lawyer's professional photo
const LAWYER_IMAGE = 'https://customer-assets.emergentagent.com/job_593b487d-f550-4b0a-bf75-88f360dcf1ed/artifacts/v9n0gc0z_openart-image_W9U7TjOj_1770431753315_raw.jpg';

const Hero = () => {
  const { t, direction } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [countUp, setCountUp] = useState({ cases: 0, recovered: 0, clients: 0, rate: 0 });

  useEffect(() => {
    setIsVisible(true);
    // Animate numbers
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCountUp({
        cases: Math.floor(250 * progress),
        recovered: Math.floor(15 * progress),
        clients: Math.floor(180 * progress),
        rate: Math.floor(87 * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0A1628 0%, #0f2444 50%, #0A1628 100%)'
      }}
    >
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gold accent lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent opacity-60" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent opacity-60" />
        
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A55A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        {/* Glowing orbs */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#C9A55A] opacity-5 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C9A55A] opacity-5 rounded-full blur-[80px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-36 lg:pb-28">
        {/* Main Grid - For Arabic: Image LEFT, Text RIGHT */}
        <div className={`flex flex-col lg:flex-row gap-12 lg:gap-16 items-center ${direction === 'rtl' ? 'lg:flex-row-reverse' : ''}`}>
          
          {/* Image - Will be on LEFT for Arabic */}
          <div className={`flex-1 ${isVisible ? 'animate-fadeIn' : 'opacity-0'} hidden lg:block`} style={{ animationDelay: '0.2s' }}>
            <div className="relative flex justify-center">
              {/* Large Decorative Background */}
              <div className="absolute -inset-8 bg-gradient-to-br from-[#C9A55A]/30 via-[#C9A55A]/10 to-transparent rounded-full blur-3xl" />
              
              {/* Main Image Container */}
              <div className="relative">
                {/* Outer Glowing Ring */}
                <div className="absolute -inset-3 bg-gradient-to-b from-[#C9A55A] via-[#D4AF37] to-[#C9A55A]/30 rounded-[2rem] opacity-50 blur-md" />
                
                {/* Image Frame */}
                <div className="relative bg-gradient-to-b from-[#C9A55A]/40 via-[#C9A55A]/20 to-[#0A1628] p-1.5 rounded-[2rem] shadow-2xl">
                  <div className="relative overflow-hidden rounded-[1.5rem]">
                    <img 
                      src={LAWYER_IMAGE}
                      alt="Dr. Qasim Hussein - Fund Recovery Lawyer"
                      className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
                      style={{ 
                        maxHeight: '550px',
                        minHeight: '450px',
                        objectPosition: 'top center'
                      }}
                    />
                    {/* Bottom Gradient Fade */}
                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/60 to-transparent" />
                    
                    {/* Name & Title Overlay */}
                    <div className="absolute bottom-6 left-0 right-0 text-center">
                      <div className="inline-block bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] px-8 py-3 rounded-full shadow-2xl">
                        <p className="text-[#0A1628] font-bold text-lg">
                          {direction === 'rtl' ? 'د. قاسم حسين' : 'Dr. Qasim Hussein'}
                        </p>
                        <p className="text-[#0A1628]/70 text-sm font-medium">
                          {direction === 'rtl' ? 'محامي دولي معتمد' : 'Certified International Lawyer'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Badge - Years Experience */}
                <div className={`absolute -top-4 ${direction === 'rtl' ? '-left-4' : '-right-4'} bg-[#0A1628] border-2 border-[#C9A55A] rounded-2xl p-4 shadow-2xl`}>
                  <div className="text-center">
                    <p className="text-[#C9A55A] font-bold text-2xl leading-none">15+</p>
                    <p className="text-gray-400 text-xs mt-1">{direction === 'rtl' ? 'سنة خبرة' : 'Years Exp.'}</p>
                  </div>
                </div>
                
                {/* Floating Badge - SRA Licensed */}
                <div className={`absolute top-1/4 ${direction === 'rtl' ? '-right-6' : '-left-6'} bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl px-4 py-3 shadow-2xl`}>
                  <div className={`flex items-center gap-2 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <Shield className="w-5 h-5 text-white" />
                    <div>
                      <span className="text-white text-sm font-bold block">SRA</span>
                      <span className="text-white/70 text-xs">{direction === 'rtl' ? 'مرخص' : 'Licensed'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Floating Badge - Success Rate */}
                <div className={`absolute bottom-1/4 ${direction === 'rtl' ? '-right-8' : '-left-8'} bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl px-4 py-3 shadow-2xl`}>
                  <div className="text-center">
                    <p className="text-white font-bold text-xl leading-none">87%</p>
                    <p className="text-white/70 text-xs mt-1">{direction === 'rtl' ? 'نسبة النجاح' : 'Success'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text Content - Will be on RIGHT for Arabic */}
          <div className={`flex-1 space-y-6 ${isVisible ? 'animate-fadeIn' : 'opacity-0'} ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
            
            {/* Premium Badge */}
            <div className={`inline-flex items-center gap-3 px-5 py-2.5 bg-[#C9A55A]/10 border border-[#C9A55A]/30 rounded-full backdrop-blur-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#C9A55A] text-[#C9A55A]" />
                ))}
              </div>
              <span className="text-sm font-medium text-[#C9A55A] tracking-wide">
                {t.ui.britishCertified}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="text-white">
                {t.hero.title.split(' ').slice(0, 3).join(' ')}
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#C9A55A] via-[#D4AF37] to-[#C9A55A] bg-clip-text text-transparent">
                {t.hero.title.split(' ').slice(3).join(' ')}
              </span>
            </h1>

            {/* Decorative Line */}
            <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
              <div className="w-16 h-0.5 bg-gradient-to-r from-[#C9A55A] to-transparent" />
              <Scale className="w-5 h-5 text-[#C9A55A]" />
              <div className="w-16 h-0.5 bg-gradient-to-l from-[#C9A55A] to-transparent" />
            </div>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-300 max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className={`flex flex-wrap gap-4 ${direction === 'rtl' ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
              <ConsultationPopup>
                <button 
                  className={`group px-8 py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-lg font-bold text-base transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,165,90,0.4)] hover:scale-105 flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}
                  data-testid="hero-cta-consultation"
                >
                  {t.hero.cta}
                  <ArrowRight className={`w-5 h-5 transition-transform ${direction === 'rtl' ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                </button>
              </ConsultationPopup>
              <button 
                onClick={() => scrollToSection('cases')}
                className="px-8 py-4 bg-transparent border-2 border-[#C9A55A]/50 text-[#C9A55A] rounded-lg font-semibold text-base transition-all duration-300 hover:bg-[#C9A55A]/10 hover:border-[#C9A55A]"
                data-testid="hero-cta-cases"
              >
                {t.hero.secondaryCta}
              </button>
            </div>

            {/* Trust Badges */}
            <div className={`flex items-center gap-6 pt-2 ${direction === 'rtl' ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
              <div className={`flex items-center gap-2 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <Shield className="w-5 h-5 text-[#C9A55A]" />
                <span className="text-sm text-gray-400">{t.ui.fcaRegulated}</span>
              </div>
              <div className="w-px h-6 bg-gray-600" />
              <div className={`flex items-center gap-2 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-400">{t.ui.noWinNoFee}</span>
              </div>
            </div>

            {/* Stats Row - Compact */}
            <div className="grid grid-cols-4 gap-3 pt-4">
              {[
                { value: `${countUp.cases}+`, label: t.hero.stats.cases, color: 'emerald' },
                { value: `$${countUp.recovered}M+`, label: t.hero.stats.recovered, color: 'blue' },
                { value: `${countUp.rate}%`, label: t.hero.stats.rate, color: 'gold' },
                { value: `${countUp.clients}+`, label: t.hero.stats.clients, color: 'purple' }
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-3 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Lawyer Image */}
        <div className={`lg:hidden mt-10 ${isVisible ? 'animate-fadeIn' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
          <div className="flex justify-center">
            <div className="relative max-w-[300px]">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A55A]/20 to-transparent rounded-full blur-2xl" />
              <div className="relative bg-gradient-to-b from-[#C9A55A]/30 to-[#0A1628] p-1 rounded-[1.5rem]">
                <div className="relative overflow-hidden rounded-[1.25rem]">
                  <img 
                    src={LAWYER_IMAGE}
                    alt="Dr. Qasim Hussein"
                    className="w-full h-auto object-cover"
                    style={{ maxHeight: '380px', objectPosition: 'top center' }}
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A1628] to-transparent" />
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <div className="inline-block bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] px-6 py-2 rounded-full shadow-lg">
                      <p className="text-[#0A1628] font-bold text-sm">
                        {direction === 'rtl' ? 'د. قاسم حسين' : 'Dr. Qasim Hussein'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-500 uppercase tracking-widest">{t.ui.scroll}</span>
          <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#C9A55A] rounded-full animate-pulse" />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default Hero;
